import React from 'react';
import { UINotificationVariant } from '../types';
import { Carousel, CenterFixed, IButtonProps, Image, INotificationsContext, LoadingSpinner } from '@gik/ui';
import { DialogContentType, DialogResponseType, IDialog, IDialogType, useDialogStoreApi } from '../Dialogs';
import SvgIcon from '../SvgIcon';
import { ModalProps } from '@gik/ui/Modal';
import KeepProportions from '@gik/ui/KeepProportions';
import EmbeddedVideo, { EmbeddedVideoProps } from '../EmbeddedVideo';
import { useBemCN } from '@gik/core/utils/bemBlock';
import { IDrawerProps } from '../Drawer';
import { translationKeys } from '../i18n/en';
import i18n from '@gik/i18n';

export interface UINotifyOptions {
  variant?: UINotificationVariant;
  clickThrough?: boolean;
  onClickThrough?: () => void;
  append?: React.ReactNode;
  prepend?: React.ReactNode;
  autoClose?: boolean;
  autoCloseTime?: number;
  close?: boolean;
}

export interface UIDialogOptions {
  title?: string;
  centered?: boolean;
  centeredTitle?: boolean;
  closable?: boolean;
  className?: string;
  overlayClassName?: string;
  buttons?: DialogContentType;
  autowidth?: boolean;
  headerId?: string;
  contentId?: string;

  onHeaderRef?(ref: HTMLDivElement): void;

  disableSystemStyling?: boolean;
  header?: DialogContentType;
  modalProps?: Partial<ModalProps>;
  footer?: React.ReactNode;
  form?: boolean;
}

export interface UIGenericDialogOptions extends UIDialogOptions {
  okText?: React.ReactNode;
  okButtonProps?: Partial<IButtonProps>;
  cancelText?: React.ReactNode;
  cancelButtonProps?: Partial<IButtonProps>;
  shouldCloseOnOverlayClick?: boolean;
  padding?: boolean;
  innerClassName?: string;
  innerStyle?: React.CSSProperties;
  onClose?: () => boolean;
  onAfterClose?: () => void;
}

export enum LightboxMedia {
  IMAGE = 'image',
  VIDEO = 'video',
}

export interface ILightboxBaseMediaType {
  mediaType: LightboxMedia;
  url: string;
  caption?: string;
}

export interface ILightboxImageMediaType extends ILightboxBaseMediaType {
  mediaType: LightboxMedia.IMAGE;
}

export interface ILightboxVideoMediaType extends ILightboxBaseMediaType {
  mediaType: LightboxMedia.VIDEO;
  videoProps?: Partial<EmbeddedVideoProps>;
}

export type LightboxMediaType = ILightboxImageMediaType | ILightboxVideoMediaType;

/**
 * UIManager takes care of triggering dialogs and notifications.
 *
 * TODO: add dialog/modal management
 */
export default class UIManager {
  notificationContext: INotificationsContext;

  constructor() {
    this._genericDialog = this._genericDialog.bind(this);
  }

  notify(content: React.ReactNode, options: UINotifyOptions = {}) {
    if (!this.notificationContext) {
      console.error('Notification store not ready');
      return;
    }
    this.notificationContext.add({ content, ...options });
  }

  notifySuccess(content: React.ReactNode, options: UINotifyOptions = {}) {
    if (!this.notificationContext) {
      console.error('Notification store not ready');
      return;
    }

    // apply sensible defaults for a success type notification
    if (options.variant === undefined) {
      options.variant = 'success';
    }
    if (options.prepend === undefined) {
      options.prepend = (
        <span className="tw-text-success-500">
          <SvgIcon name="CheckCircleOutline" set="hero-icons" />
        </span>
      );
    }

    this.notificationContext.add({ content, ...options });
  }

  notifyWarning(content: React.ReactNode, options: UINotifyOptions = {}) {
    if (!this.notificationContext) {
      console.error('Notification store not ready');
      return;
    }

    // apply sensible defaults for a success type notification
    if (options.variant === undefined) {
      options.variant = 'warning';
    }
    if (options.prepend === undefined) {
      options.prepend = (
        <span className="tw-text-warn-500">
          <SvgIcon name="ExclamationCircleOutline" set="hero-icons" />
        </span>
      );
    }

    this.notificationContext.add({ content, ...options });
  }

  notifyError(content: React.ReactNode, options: UINotifyOptions = {}) {
    if (!this.notificationContext) {
      console.error('Notification store not ready');
      return;
    }

    // apply sensible defaults for a success type notification
    if (options.variant === undefined) {
      options.variant = 'error';
    }
    if (options.prepend === undefined) {
      options.prepend = (
        <span className="tw-text-danger-500">
          <SvgIcon name="ExclamationCircleOutline" set="hero-icons" />
        </span>
      );
    }

    this.notificationContext.add({ content, ...options });
  }

  notifyPremiumPageLocked() {
    this.notify(i18n.t(translationKeys.PremiumLockedNotification), {
      variant: 'premium-locked',
      prepend: <SvgIcon name="LockClosed" size="lg" />,
    });
  }

  notifyPremiumPageUnlocked() {
    this.notify(i18n.t(translationKeys.PremiumUnlockedNotification), {
      variant: 'premium-unlocked',
      prepend: <SvgIcon name="LockOpen" size="lg" />,
    });
  }

  clearNotification(notificationId: string) {
    if (!this.notificationContext) {
      console.error('Notification store not ready');
      return;
    }
    this.notificationContext.remove(notificationId);
  }

  clearAllNotifications() {
    if (!this.notificationContext) {
      console.error('Notification store not ready');
      return;
    }
    this.notificationContext.removeAll();
  }

  alert(content: React.ReactNode, options?: UIGenericDialogOptions): Promise<boolean> {
    return this._genericDialog('alert', content, options);
  }

  error(content: React.ReactNode, options?: UIGenericDialogOptions): Promise<boolean> {
    return this._genericDialog('error', content, options);
  }

  warn(content: React.ReactNode, options?: UIGenericDialogOptions): Promise<boolean> {
    return this._genericDialog('warn', content, options);
  }

  success(content: React.ReactNode, options?: UIGenericDialogOptions): Promise<boolean> {
    return this._genericDialog('success', content, options);
  }

  confirm(content: React.ReactNode, options?: UIGenericDialogOptions): Promise<boolean> {
    return this._genericDialog('confirm', content, options);
  }

  info(content: React.ReactNode, options?: UIGenericDialogOptions): Promise<boolean> {
    return this._genericDialog('info', content, options);
  }

  dialog<T extends DialogResponseType = boolean>(
    content: DialogContentType,
    options: UIGenericDialogOptions
  ): Promise<T> {
    return this._genericDialog('dialog', content, options);
  }

  lightbox<T extends DialogResponseType = boolean>(
    media: LightboxMediaType[],
    options: UIGenericDialogOptions = {},
    initialSlide = 0
  ): Promise<T> {
    return this._genericDialog('dialog', <LightboxContent initialSlide={initialSlide} media={media} />, {
      ...options,
      closable: true,
      centeredTitle: true,
      className: `gik-modal--lightbox ${options.className || ''}`,
      overlayClassName: 'gik-overlay--lightbox',
    });
  }

  loadingSpinnerOverlay<T extends DialogResponseType = boolean>(options: UIGenericDialogOptions = {}): Function {
    let closeFn: Function;

    this._genericDialog(
      'dialog',
      ({ close }) => {
        closeFn = close;
        return (
          <CenterFixed>
            <LoadingSpinner />
          </CenterFixed>
        );
      },
      {
        ...options,
        closable: false,
        className: `gik-modal--loading-spinner ${options.className || ''}`,
      }
    );

    return closeFn;
  }

  closeDialog(dialogId?: string, response?: DialogResponseType) {
    useDialogStoreApi.getState().close(dialogId, response);
  }

  closeAllDialogs(response?: DialogResponseType) {
    useDialogStoreApi.getState().closeAll(response);
  }

  modalSheet(content: React.ReactNode | (() => React.ReactNode), options?: IDrawerProps) {
    useDialogStoreApi.getState().addModalSheet({
      children: typeof content === 'function' ? content() : content,
      ...options,
    });
  }

  closeModalSheet() {
    // TODO: move check into dialogs store
    if (!useDialogStoreApi.getState().sheetIsOpen) return;
    useDialogStoreApi.getState().closeModalSheet();
    // TODO: move into dialogs store, this should be an atomic operation
    setTimeout(() => {
      useDialogStoreApi.getState().removeModalSheet();
    }, 500);
  }

  get modalSheetHasChanges() {
    return useDialogStoreApi.getState().modalSheetHasChanges;
  }

  set modalSheetHasChanges(hasChanges: boolean) {
    useDialogStoreApi.getState().setModalSheetHasChanges(hasChanges);
  }

  private _genericDialog<T extends DialogResponseType>(
    type: IDialogType,
    content: React.ReactNode,
    options: UIGenericDialogOptions = {}
  ): Promise<T> {
    return this._wrapWithDialogPromise({
      content,
      type,
      padding: true,
      closable: false,
      ...options,
    });
  }

  private _wrapWithDialogPromise<T extends DialogResponseType>(item: IDialog) {
    return new Promise<T>((resolve, reject) => {
      const itemWithPromise = {
        ...item,
        onPromiseResolve: resolve,
        onPromiseReject: reject,
      };
      const itemId = useDialogStoreApi.getState().add(itemWithPromise);

      // NOTE: The timeout is needed here to allow to backdrop to render first
      // before the enter animation is triggered.
      // TODO: maybe changing the Backdrop from a transition to an animation would fix this issue
      // TODO: move into the dialog store, adding and opening the dialog should be an atomic operation
      setTimeout(() => {
        useDialogStoreApi.getState().open(itemId);
      }, 1);
    });
  }
}

interface ILightboxContentProps {
  media: LightboxMediaType[];
  initialSlide?: number;
}

function LightboxContent({ media = [], initialSlide = 0 }: ILightboxContentProps) {
  const bem = useBemCN('lightbox-content');

  function mediaMap(m, key) {
    let content;
    switch (m.mediaType) {
      case LightboxMedia.VIDEO:
        content = (
          <KeepProportions containType="modal">
            <EmbeddedVideo url={m.url} {...m.videoProps} />
          </KeepProportions>
        );
        break;
      case LightboxMedia.IMAGE:
        content = <Image src={m.url} />;
        break;
    }

    return (
      <div {...bem('slide')} key={key}>
        {content}
        {m.caption && <p {...bem('caption')}>{m.caption}</p>}
      </div>
    );
  }

  return (
    <>
      <Carousel {...bem()} initialSlide={initialSlide}>
        {media.map(mediaMap)}
      </Carousel>
      <LoadingSpinner {...bem('loading')} />
    </>
  );
}
